import {
  StatusLabel,
  type StatusLabelStatus,
  StatusLabelStatuses,
} from '@procurify/ui'
import { type MessageDescriptor } from 'react-intl'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import {
  type GlobalSearchResultStatusEnum,
  GlobalSearchResultStatuses,
  type GlobalSearchResultTypeEnum,
  GlobalSearchResultTypes,
} from '../../../../../../value-objects/enums'

const STATUS_LABEL_TYPE_MAP: Partial<
  Record<
    GlobalSearchResultTypeEnum,
    Partial<Record<GlobalSearchResultStatusEnum, StatusLabelStatus>>
  >
> = {
  [GlobalSearchResultTypes.ORDER_REQUEST]: {
    [GlobalSearchResultStatuses.PENDING_APPROVAL]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.APPROVED]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.DENIED]: StatusLabelStatuses.WARNING,
    [GlobalSearchResultStatuses.PURCHASED]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.RECEIVED]: StatusLabelStatuses.SUCCESS,
  },
  [GlobalSearchResultTypes.PURCHASE_ORDER]: {
    [GlobalSearchResultStatuses.OPEN]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.REOPENED]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.CANCELLED]: StatusLabelStatuses.WARNING,
    [GlobalSearchResultStatuses.CLOSED]: StatusLabelStatuses.SUCCESS,
    [GlobalSearchResultStatuses.PAID]: StatusLabelStatuses.SUCCESS,
  },
  [GlobalSearchResultTypes.EXPENSE_REQUEST]: {
    [GlobalSearchResultStatuses.PENDING_APPROVAL]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.APPROVED]: StatusLabelStatuses.SUCCESS,
    [GlobalSearchResultStatuses.DENIED]: StatusLabelStatuses.WARNING,
  },
  [GlobalSearchResultTypes.TRAVEL_REQUEST]: {
    [GlobalSearchResultStatuses.PENDING_APPROVAL]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.APPROVED]: StatusLabelStatuses.SUCCESS,
    [GlobalSearchResultStatuses.DENIED]: StatusLabelStatuses.WARNING,
  },
  [GlobalSearchResultTypes.BILL]: {
    [GlobalSearchResultStatuses.DRAFT]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.PENDING_APPROVAL]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.APPROVED]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.DENIED]: StatusLabelStatuses.WARNING,
    [GlobalSearchResultStatuses.PAID]: StatusLabelStatuses.SUCCESS,
  },
  [GlobalSearchResultTypes.PAYMENT]: {
    [GlobalSearchResultStatuses.DRAFT]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.PENDING_APPROVAL]: StatusLabelStatuses.INACTIVE,
    [GlobalSearchResultStatuses.APPROVED]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.DENIED]: StatusLabelStatuses.WARNING,
    [GlobalSearchResultStatuses.SCHEDULED]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.PROCESSING]: StatusLabelStatuses.INFO,
    [GlobalSearchResultStatuses.PAID]: StatusLabelStatuses.SUCCESS,
    [GlobalSearchResultStatuses.FAILED]: StatusLabelStatuses.ERROR,
  },
}

const STATUS_LABEL_MESSAGE_MAP: Record<
  GlobalSearchResultStatusEnum,
  MessageDescriptor
> = {
  [GlobalSearchResultStatuses.PENDING_APPROVAL]: messages.pendingApprovalLabel,
  [GlobalSearchResultStatuses.APPROVED]: messages.approvedLabel,
  [GlobalSearchResultStatuses.DENIED]: messages.deniedLabel,
  [GlobalSearchResultStatuses.PURCHASED]: messages.purchasedLabel,
  [GlobalSearchResultStatuses.RECEIVED]: messages.receivedLabel,
  [GlobalSearchResultStatuses.OPEN]: messages.openLabel,
  [GlobalSearchResultStatuses.REOPENED]: messages.reopenedLabel,
  [GlobalSearchResultStatuses.CANCELLED]: messages.cancelledLabel,
  [GlobalSearchResultStatuses.CLOSED]: messages.closedLabel,
  [GlobalSearchResultStatuses.PAID]: messages.paidLabel,
  [GlobalSearchResultStatuses.DRAFT]: messages.draftLabel,
  [GlobalSearchResultStatuses.SCHEDULED]: messages.scheduledLabel,
  [GlobalSearchResultStatuses.PROCESSING]: messages.processingLabel,
  [GlobalSearchResultStatuses.FAILED]: messages.failedLabel,
}

const getStatusLabelType = (
  resultType: GlobalSearchResultTypeEnum,
  status: GlobalSearchResultStatusEnum
): Nullable<StatusLabelStatus> => {
  const typeMap = STATUS_LABEL_TYPE_MAP[resultType]

  return typeMap ? (typeMap[status] ?? null) : null
}

const getStatusMessage = (
  status: GlobalSearchResultStatusEnum
): MessageDescriptor => {
  return STATUS_LABEL_MESSAGE_MAP[status]
}

interface IGlobalSearchListItemStatusLabelProps {
  resultType: GlobalSearchResultTypeEnum
  status: GlobalSearchResultStatusEnum
}

export const GlobalSearchListItemStatusLabel = ({
  resultType,
  status,
}: IGlobalSearchListItemStatusLabelProps) => {
  const intl = useProcurifyIntl()
  const statusLabelType = getStatusLabelType(resultType, status)

  if (!statusLabelType) {
    return null
  }

  return (
    <StatusLabel
      type={statusLabelType}
      text={intl.formatMessage(getStatusMessage(status))}
      fontSize={12}
      paddingSize='small'
    />
  )
}
